/**
 * VirtualizedShiftLayer - Efficient shift rendering for virtualized calendar days
 * Renders all shifts for a single day with proper positioning
 */

import React, { useMemo, useCallback, memo, useRef, useState, useEffect } from 'react';
import { DayShift, Employee } from '@/types';
import { ShiftRow } from '@/lib/scheduling/shift-utils';
import { ROW_HEIGHT_PX, INTERVALS_PER_HOUR } from '@/components/shifts/constants';
import DraggableShift from '@/components/shifts/DraggableShift';

interface VirtualizedShiftLayerProps {
  dayDate: Date;
  dayKey: string;
  organizedShifts: ShiftRow[];
  numRows: number;
  isToday: boolean;

  // Shift rendering props
  shifts: DayShift[];
  employees: Employee[];
  businessId?: string | null;

  // Display configuration
  totalDisplayHours: number;
  effectiveDisplayStartTime: Date;
  effectiveDisplayEndTime: Date;
  hourCellWidthPx: number;
  hideDayColumn?: boolean;

  // Event handlers
  onAssignEmployee?: (shiftId: string, employeeId: string | null) => void;
  onRepositionShift?: (shiftId: string, newStartTime: Date, newEndTime: Date) => Promise<void>;
  onOptimisticPositionUpdate?: (shiftId: string, newStartTime: Date, newEndTime: Date) => void;
  onOptimisticPositionClear?: (shiftId: string) => void;
  onShiftSelect?: (shiftId: string, selected: boolean) => void;
  onDeleteShift?: (shiftId: string) => void;
  selectedShiftIds?: Set<string>;
  actionsPanelMode?: string;
  onOpenActionsPanel?: () => void;
  onSetActionsPanelMode?: (mode: string) => void;
  isActionsPanelOpen?: boolean;

  scrollContainerRef?: React.RefObject<HTMLDivElement>;
}

const VirtualizedShiftLayer: React.FC<VirtualizedShiftLayerProps> = ({
  dayDate,
  dayKey,
  organizedShifts,
  numRows,
  isToday,
  shifts,
  employees,
  businessId,
  totalDisplayHours,
  effectiveDisplayStartTime,
  effectiveDisplayEndTime,
  hourCellWidthPx,
  hideDayColumn = false,
  onAssignEmployee,
  onRepositionShift,
  onOptimisticPositionUpdate,
  onOptimisticPositionClear,
  onShiftSelect,
  onDeleteShift,
  selectedShiftIds,
  actionsPanelMode,
  onOpenActionsPanel,
  onSetActionsPanelMode,
  isActionsPanelOpen = false,
  scrollContainerRef,
}) => {
  // Flatten organized shifts for rendering
  const flatShifts = useMemo(() => {
    return organizedShifts.flatMap((row) =>
      row.shifts.map((shift) => ({
        shift,
        rowIndex: row.rowIndex,
      }))
    );
  }, [organizedShifts]);

  // Ref and state for measuring actual layer width
  const layerRef = useRef<HTMLDivElement>(null);
  const [measuredWidth, setMeasuredWidth] = useState<number>(0);
  const lastStableWidthRef = useRef<number>(0); // Track last stable width to prevent micro-fluctuations

  // Measure actual layer width using ResizeObserver
  useEffect(() => {
    if (!layerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = Math.round(entry.contentRect.width);
        // Only update if change is significant (> 2px) to prevent shift width "bouncing"
        const widthDiff = Math.abs(width - lastStableWidthRef.current);
        if (widthDiff > 2 || lastStableWidthRef.current === 0) {
          lastStableWidthRef.current = width;
          setMeasuredWidth(width);
        }
      }
    });

    resizeObserver.observe(layerRef.current);
    // Initial measurement
    const initialWidth = Math.round(layerRef.current.getBoundingClientRect().width);
    lastStableWidthRef.current = initialWidth;
    setMeasuredWidth(initialWidth);

    return () => resizeObserver.disconnect();
  }, []);

  // Calculate actual cell width from measured layer width
  // Layer spans column 1 to -1 (full grid width including day column)
  const actualCellWidth = useMemo(() => {
    if (!measuredWidth || !totalDisplayHours || totalDisplayHours <= 0) {
      return hourCellWidthPx; // Fallback to prop value
    }
    const totalIntervals = Math.ceil(totalDisplayHours * INTERVALS_PER_HOUR);
    const dayColumnWidth = hideDayColumn ? 0 : 32;
    const timeAreaWidth = measuredWidth - dayColumnWidth;
    return timeAreaWidth / totalIntervals;
  }, [measuredWidth, totalDisplayHours, hideDayColumn, hourCellWidthPx]);

  // Stable click handler that extracts shift ID from data attribute
  // This prevents creating new function closures for each shift
  const handleShiftClick = useCallback(
    (e: React.MouseEvent) => {
      if (!onShiftSelect) return;

      // Find the shift element and get its ID from data attribute
      const target = e.currentTarget as HTMLElement;
      const shiftId = target.getAttribute('data-shift-id');

      if (!shiftId) {
        console.error('Shift ID not found on element');
        return;
      }

      try {
        onShiftSelect(shiftId, !selectedShiftIds?.has(shiftId));
      } catch (error) {
        console.error('Error selecting shift:', error);
      }
    },
    [onShiftSelect, selectedShiftIds]
  );

  // Guard: Don't render shifts until container is properly measured
  // Use actualCellWidth which combines parent's hourCellWidthPx with component's own measuredWidth
  // This allows rendering when the component measures itself, even if parent hasn't measured yet
  if (!actualCellWidth || actualCellWidth <= 0) {
    return (
      <div
        ref={layerRef}
        className="virtualized-shift-layer"
        style={{
          gridColumn: '1 / -1',
          gridRow: `1 / ${numRows + 1}`,
          position: 'relative',
          height: `${numRows * ROW_HEIGHT_PX}px`,
        }}
        data-shift-layer="true"
      >
        {/* Placeholder while waiting for measurements */}
      </div>
    );
  }

  return (
    <div
      ref={layerRef}
      className="virtualized-shift-layer"
      style={{
        // Always span from column 1 - shift calculations include day column offset
        gridColumn: '1 / -1',
        gridRow: `1 / ${numRows + 1}`, // Span all rows
        position: 'relative', // For absolutely positioned shift children
        pointerEvents: 'none', // Allow clicks to pass through container to hour cells
        height: `${numRows * ROW_HEIGHT_PX}px`,
        // Note: z-index removed to prevent stacking context that traps tooltips
      }}
      data-shift-layer="true"
    >
      {flatShifts.map(({ shift, rowIndex }) => (
        <DraggableShift
          key={`shift-${shift.id}-${dayKey}-${rowIndex}`}
          shift={shift}
          displayStartTime={effectiveDisplayStartTime}
          displayEndTime={effectiveDisplayEndTime}
          totalDisplayHours={totalDisplayHours}
          hourCellWidthPx={actualCellWidth}
          hideDayColumn={hideDayColumn}
          rowIndex={rowIndex}
          businessId={businessId}
          employees={employees}
          allDayShifts={shifts}
          onAssignEmployee={onAssignEmployee}
          calendarVariant="full"
          scrollContainerRef={scrollContainerRef}
          onClick={onShiftSelect ? handleShiftClick : undefined}
          onShiftSelect={onShiftSelect}
          onDelete={onDeleteShift}
          selectedShiftIds={selectedShiftIds}
          actionsPanelMode={actionsPanelMode}
          onOpenActionsPanel={onOpenActionsPanel}
          onSetActionsPanelMode={onSetActionsPanelMode}
          isActionsPanelOpen={isActionsPanelOpen}
          isHistorical={shift.isHistorical}
          onReposition={onRepositionShift}
          onOptimisticPositionUpdate={onOptimisticPositionUpdate}
          onOptimisticPositionClear={onOptimisticPositionClear}
        />
      ))}
    </div>
  );
};

// Wrap with memo and custom comparison to prevent unnecessary re-renders
export default memo(VirtualizedShiftLayer, (prevProps, nextProps) => {
  // Re-render if shifts change (reference or length)
  if (
    prevProps.shifts !== nextProps.shifts ||
    prevProps.organizedShifts !== nextProps.organizedShifts ||
    prevProps.shifts.length !== nextProps.shifts.length
  ) {
    return false;
  }

  // Re-render if selection state changes
  if (
    prevProps.selectedShiftIds !== nextProps.selectedShiftIds ||
    prevProps.actionsPanelMode !== nextProps.actionsPanelMode ||
    prevProps.isActionsPanelOpen !== nextProps.isActionsPanelOpen
  ) {
    return false;
  }

  // Re-render if display configuration changes
  if (
    prevProps.hourCellWidthPx !== nextProps.hourCellWidthPx ||
    prevProps.totalDisplayHours !== nextProps.totalDisplayHours ||
    prevProps.hideDayColumn !== nextProps.hideDayColumn ||
    prevProps.effectiveDisplayStartTime?.getTime() !== nextProps.effectiveDisplayStartTime?.getTime() ||
    prevProps.effectiveDisplayEndTime?.getTime() !== nextProps.effectiveDisplayEndTime?.getTime()
  ) {
    return false;
  }

  // Re-render if day changes
  if (
    prevProps.dayDate.getTime() !== nextProps.dayDate.getTime() ||
    prevProps.numRows !== nextProps.numRows ||
    prevProps.isToday !== nextProps.isToday
  ) {
    return false;
  }

  // Don't re-render for other prop changes (like function references)
  return true;
});
